import { UserPermissionsContext } from '@client/Context/UserPermissions';
import {
    ClickableLink,
    ExtendedColumn,
    HoveroverButton,
    LogLevel,
    NonSelectableRow,
    showBanner,
    StyledPill,
    User,
} from '@sprint/sprint-react-components';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { faClock } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { CampaignsRequest } from '../../Api/CampaignsRequest';
import { KnowledgeBaseUrlKey, KnowledgeBaseUrls } from '../../HelperFunctions/KnowledgeBaseUrls';
import UniqueKeyBuilder from '../../HelperFunctions/UniqueKeyBuilder';
import UserPermissionsHelper from '../../HelperFunctions/UserPermissionsHelper';
import { RepositoryFactoryContext, UserContext } from '../../index';
import CampaignType from '../../Models/CampaignType';
import { CampaignStatus, HTMLColors, UniqueKeyType } from '../../Models/Enums';
import CampusDataGrid, {
    ActionBarMeta,
    AddSispMeta,
    DataGridMeta,
    DeleteModalMeta,
    PromptMeta,
} from '../CampusDataGrid';
import ClientTagsColumn from '../ClientTags/ClientTagsColumn';
import CampaignsAddRedirect from './CampaignsAddRedirect';
import CampaignsCancelModal from './CampaignsCancelModal';
import CampaignsDeleteModal from './CampaignsDeleteModal';
import CampaignsEditSisp from './CampaignsEditSisp';
import CampaignsStopModal from './CampaignsStopModal';
import SentViaBadge from '../../Components/SentViaBadge/SentViaBadge';

interface Props {
    searchFilterPlaceholder: string;
    dataGridUniqueKey: string;
    dataGridEntitySingular: string;
    dataGridEntityPlural: string;
    onlyShowCampaignsWithNoReport?: boolean;
    /** CC-7107 Take this flag out when feature is fully released */
    archivedOnly?: boolean;
}

enum CampaignsColumnKey {
    ID,
    EMAIL,
    LIST,
    TYPE,
    SCHEDULED,
    STATUS,
    SENT,
    OPENS,
    CLICKS,
    SENT_VIA,
    ESTIMATED_COMPLETION,
}

const CampaignsTable: FunctionComponent<Props> = (props: Props) => {
    const editSispUniqueKey = UniqueKeyBuilder.make(props.dataGridUniqueKey, UniqueKeyType.EDIT_SISP);

    const campaignsRepository = useContext(RepositoryFactoryContext).getApiRepository(new CampaignsRequest());

    const businessDetailsCompleted = JSON.parse(
        String((document.getElementById('business_details_completed') as HTMLInputElement).value),
    );
    const canSlowSend = JSON.parse(
        String((document.getElementById('can_slow_send') as HTMLInputElement)?.value || '0'),
    );
    const permissions = useContext(UserPermissionsContext);
    const user: User = useContext(UserContext);

    // State: General
    const [reload, setReload] = useState<boolean>(false);

    useEffect(() => {
        if (reload) setReload(false);
    }, [reload]);

    // State: Cancel Campaign Modal
    const [cancelModalShown, setCancelModalShown] = useState<boolean>(false);
    const [cancelCampaignId, setCancelCampaignId] = useState<number>();

    // State: Stop Campaign Modal
    const [stopModalShown, setStopModalShown] = useState<boolean>(false);
    const [stopCampaignId, setStopCampaignId] = useState<number>();
    const [stopCampaignEmail, setStopCampaignEmail] = useState<string>('');

    // State: Columns
    const Columns: Record<CampaignsColumnKey, ExtendedColumn> = {
        [CampaignsColumnKey.ID]: {
            key: 'id',
            name: 'ID',
            sortable: false,
        },
        [CampaignsColumnKey.EMAIL]: {
            key: 'email',
            name: 'Email',
            sortable: true,
            width: '6fr',
            renderCell: (formatterProps) => {
                const id = (formatterProps.row as CampaignType).id;
                const email = (formatterProps.row as CampaignType).email;
                const status = (formatterProps.row as CampaignType).status;

                const handleCancelButtonClick = () => {
                    setCancelCampaignId(id);
                    setCancelModalShown(true);
                };

                const handleStopButtonClick = () => {
                    setStopCampaignId(id);
                    setStopCampaignEmail(email.value);
                    setStopModalShown(true);
                };

                return (
                    // Lay the cell out as a row so the subject takes the remaining width and
                    // truncates with an ellipsis, keeping the hover action buttons (which never
                    // shrink) in view however long the subject is.
                    <span style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                        <span
                            style={{
                                flex: '1 1 auto',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {!props.onlyShowCampaignsWithNoReport && status === CampaignStatus.SENT ? (
                                <ClickableLink placeholder={email.value} url={email.url} />
                            ) : (
                                email.value
                            )}
                        </span>
                        <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                            <HoveroverButton
                                contents="Edit"
                                showHoverover={status === CampaignStatus.SCHEDULED}
                                eventBusMessageTarget={editSispUniqueKey}
                                eventBusMessage={formatterProps.row as CampaignType}
                            />
                            <HoveroverButton
                                contents="Cancel"
                                showHoverover={status === CampaignStatus.SCHEDULED}
                                handleClick={handleCancelButtonClick}
                            />
                            <HoveroverButton
                                contents="Abandon"
                                showHoverover={(formatterProps.row as CampaignType).can_stop}
                                handleClick={handleStopButtonClick}
                            />
                        </span>
                    </span>
                );
            },
        },
        [CampaignsColumnKey.LIST]: {
            key: 'list',
            name: 'List',
            sortable: true,
            width: '3fr',
            renderCell: (props) => {
                const list = (props.row as CampaignType).list;
                return <ClickableLink placeholder={list.value} url={list.url} />;
            },
        },
        [CampaignsColumnKey.TYPE]: {
            key: 'type',
            name: 'Type',
            sortable: true,
            width: '1.75fr',
        },
        [CampaignsColumnKey.SCHEDULED]: {
            key: 'scheduled',
            name: 'Scheduled',
            sortable: true,
            width: '2fr',
        },
        [CampaignsColumnKey.STATUS]: {
            key: 'status',
            name: 'Status',
            sortable: false,
            width: '1.75fr',
            renderCell: (formatterProps) => {
                const row = formatterProps.row as CampaignType;
                const status = row.status;

                if (props.onlyShowCampaignsWithNoReport && status == CampaignStatus.SENT) {
                    return (
                        <OverlayTrigger
                            overlay={
                                <Tooltip id="builtin-tooltip">
                                    This campaign has sent. A report is being processed and will soon be available in
                                    the Reporting tab.
                                </Tooltip>
                            }
                            trigger={['hover', 'focus']}
                        >
                            <span style={{ display: 'inline-block' }}>
                                <StyledPill
                                    cellContent={CampaignStatus.AWAITING_REPORT}
                                    style={{ backgroundColor: HTMLColors.SUCCESS }}
                                />
                            </span>
                        </OverlayTrigger>
                    );
                }

                const findPillStyle = (status: string): string => {
                    switch (status) {
                        case CampaignStatus.SENT:
                            return HTMLColors.SUCCESS;
                        case CampaignStatus.SCHEDULED:
                            return HTMLColors.PRIMARY;
                        case CampaignStatus.ABANDONED:
                            return HTMLColors.GREY_SCALE;
                        default:
                            return HTMLColors.WARNING;
                    }
                };
                const pill = <StyledPill cellContent={status} style={{ backgroundColor: findPillStyle(status) }} />;

                // A rate-paced send that is sending but currently outside its window/days is on hold;
                // flag it with a clock and explain that it resumes when the window reopens.
                if (status === CampaignStatus.SENDING && row.paused_outside_window) {
                    const windowText =
                        row.send_window_start && row.send_window_end
                            ? ` (${row.send_window_start.slice(0, 5)}–${row.send_window_end.slice(0, 5)})`
                            : '';
                    const pausedPopover = (
                        <Popover id={`sending-paused-${row.id}`}>
                            <Popover.Title>Sending paused</Popover.Title>
                            <Popover.Content>
                                This campaign is outside its sending window{windowText}, so sending is paused and will
                                resume {row.paused_resume_label ?? 'when the window next opens'}.
                            </Popover.Content>
                        </Popover>
                    );
                    return (
                        <OverlayTrigger overlay={pausedPopover} trigger={['hover', 'focus']} placement="top">
                            <span
                                className="pill"
                                style={{ backgroundColor: findPillStyle(status), cursor: 'help' }}
                            >
                                {status} <FontAwesomeIcon icon={faClock} style={{ marginLeft: '3px' }} />
                            </span>
                        </OverlayTrigger>
                    );
                }

                return pill;
            },
        },
        [CampaignsColumnKey.SENT]: {
            key: 'sent',
            name: 'Sent',
            sortable: false,
            width: '1fr',
        },
        [CampaignsColumnKey.OPENS]: {
            key: 'opens',
            name: 'Opens',
            sortable: false,
            width: '1.25fr',
            renderCell: (formatterProps) => {
                const data = formatterProps.row as CampaignType;
                return data.status === CampaignStatus.SENT
                    ? data.tracking.opens
                        ? data.opens
                        : 'N/A'
                    : data.tracking.opens
                      ? 'Yes'
                      : 'No';
            },
        },
        [CampaignsColumnKey.CLICKS]: {
            key: 'clicks',
            name: 'Clicks',
            sortable: false,
            width: '1.25fr',
            renderCell: (formatterProps) => {
                const data = formatterProps.row as CampaignType;
                return data.status === CampaignStatus.SENT
                    ? data.tracking.clicks
                        ? data.clicks
                        : data.tracking.utm_source
                          ? 'UTM'
                          : 'N/A'
                    : data.tracking.clicks
                      ? 'Yes'
                      : data.tracking.utm_source
                        ? 'UTM'
                        : 'No';
            },
        },
        [CampaignsColumnKey.SENT_VIA]: {
            key: 'used_dedicated',
            // The sending view lists campaigns that have not finished sending, so it reads "Send Via";
            // the reports view lists completed sends and reads "Sent Via".
            name: props.onlyShowCampaignsWithNoReport ? 'Send Via' : 'Sent Via',
            sortable: false,
            width: '1.25fr',
            renderCell: (formatterProps) => {
                // Driven by the blocks actually built: a both campaign that came in under a day's
                // dedicated ceiling sent only via dedicated, so it shows a single pill.
                const row = formatterProps.row as CampaignType;
                return (
                    <SentViaBadge
                        usedDedicated={row.used_dedicated}
                        usedShared={row.used_shared}
                        dailySendLimit={row.daily_send_limit}
                        sendWindowStart={row.send_window_start}
                        sendWindowEnd={row.send_window_end}
                        sendDays={row.send_days}
                        idSuffix={row.id}
                    />
                );
            },
        },
        [CampaignsColumnKey.ESTIMATED_COMPLETION]: {
            key: 'estimated_completion',
            name: 'Est. Completion',
            sortable: false,
            width: '2fr',
        },
    };

    const DefaultColumns: ExtendedColumn[] = [
        Columns[CampaignsColumnKey.EMAIL],
        Columns[CampaignsColumnKey.LIST],
        Columns[CampaignsColumnKey.TYPE],
        ...(canSlowSend ? [Columns[CampaignsColumnKey.SENT_VIA]] : []),
        Columns[CampaignsColumnKey.SCHEDULED],
        ...(canSlowSend ? [Columns[CampaignsColumnKey.ESTIMATED_COMPLETION]] : []),
        Columns[CampaignsColumnKey.STATUS],
        Columns[CampaignsColumnKey.SENT],
        Columns[CampaignsColumnKey.OPENS],
        Columns[CampaignsColumnKey.CLICKS],
    ];

    const stopCampaign = (id: number): Promise<boolean> => {
        return campaignsRepository
            .post_action('stop_campaign', id)
            .then((success: any) => {
                showBanner({ message: success.data.message, level: LogLevel.SUCCESS });
                setReload(true);
                return true;
            })
            .catch((err: any) => {
                showBanner({ message: err?.message ?? err, level: LogLevel.ERROR });
                return false;
            });
    };

    // DataGridActionBar
    const cancelCampaign = (id: number): Promise<boolean> => {
        return campaignsRepository
            .post_action('cancel_campaign', id)
            .then((success: any) => {
                showBanner({
                    message: success.data.message,
                    level: LogLevel.SUCCESS,
                });
                setReload(true);
                return true;
            })
            .catch((err: any) => {
                showBanner({
                    message: err?.message ?? err,
                    level: LogLevel.ERROR,
                });
                return false;
            });
    };

    const findNonSelectableRows = (rows: any): NonSelectableRow[] | null => {
        return rows
            ?.filter((row: any) => (row as CampaignType).status === CampaignStatus.SENDING)
            .map((row: any) => ({ id: row.id, reason: 'In the process of sending' }));
    };

    const externalParams: any = {};

    if (props.onlyShowCampaignsWithNoReport) {
        externalParams.onlyCampaignsWithNoReport = props.onlyShowCampaignsWithNoReport;
        if (permissions.campaignsCustomTags.isEnabled) {
            DefaultColumns.push(ClientTagsColumn);
        }
    }

    if (props.archivedOnly) {
        externalParams.archivedOnly = props.archivedOnly;
    }

    const dataGridMeta: DataGridMeta = {
        uniqueKey: props.dataGridUniqueKey,
        entitySingular: props.dataGridEntitySingular,
        entityPlural: props.dataGridEntityPlural,
        columnOptions: Columns,
        defaultColumns: DefaultColumns,
        frozenColumns: [],
        defaultSortColumn: 'id',
        forceReload: reload,
        nonSelectableRows: findNonSelectableRows,
        // Silently refresh the rows once a minute (no loading spinner) so a campaign flipping to
        // Sending - and a sending one's progress to Sent - shows without a manual reload. Skipped on
        // the archived view, where nothing changes. The library pauses this while rows are selected.
        autoRefreshInterval: props.archivedOnly ? undefined : 60,
        externalExtendedParameters: externalParams,
    };

    const actionBarMeta: ActionBarMeta = {
        searchPlaceHolder: props.searchFilterPlaceholder,
        includeCounts: true,
    };

    const addSispMeta: AddSispMeta = {
        key: UniqueKeyBuilder.make(props.dataGridUniqueKey, UniqueKeyType.ADD_SISP),
        sisp: CampaignsAddRedirect,
    };

    const deleteModalMeta: DeleteModalMeta = {
        modal: CampaignsDeleteModal,
    };

    const promptMeta: PromptMeta = {
        icon: '/assets/application/img/prompts/no_campaigns.png',
        iconHeight: 180,
        helpCentreLink: KnowledgeBaseUrls.get(KnowledgeBaseUrlKey.EMAILS),
    };

    const businessDetailsNeededAllowedPromptMeta: PromptMeta = {
        icon: '/assets/application/img/prompts/no_emails.png',
        iconHeight: 180,
        helpCentreLink: KnowledgeBaseUrls.get(KnowledgeBaseUrlKey.CREATE_EMAIL),
        header: "You Can't Send Emails Yet",
        addLineText: 'To start sending emails some business settings need adding',
        addButtonLabel: 'Add Business Details',
        addOnClickOverride: () => {
            window.location.href = '/settings/business';
        },
    };

    const businessDetailsNeededForbiddenPromptMeta: PromptMeta = {
        icon: '/assets/application/img/prompts/no_emails.png',
        iconHeight: 180,
        helpCentreLink: KnowledgeBaseUrls.get(KnowledgeBaseUrlKey.CREATE_EMAIL),
        header: "You Can't Send Emails Yet",
        additionalSubtext: [
            'To start sending emails some business settings need adding to Campus.',
            "Unfortunately your user level doesn't allow you to do this. You'll need to ask an Admin or Manager user to add them first.",
        ],
        hideAdd: true,
    };

    const returnCorrectBusinessDetailsNeededPrompt = (): PromptMeta => {
        return UserPermissionsHelper.isUserAnAdminOrManager(user)
            ? businessDetailsNeededAllowedPromptMeta
            : businessDetailsNeededForbiddenPromptMeta;
    };

    return (
        <>
            {cancelCampaignId && (
                <CampaignsCancelModal
                    modalShown={cancelModalShown}
                    cancelId={cancelCampaignId}
                    closeModal={() => setCancelModalShown(false)}
                    onSubmit={cancelCampaign}
                />
            )}
            {stopCampaignId && (
                <CampaignsStopModal
                    modalShown={stopModalShown}
                    stopId={stopCampaignId}
                    stopEmail={stopCampaignEmail}
                    closeModal={() => setStopModalShown(false)}
                    onSubmit={stopCampaign}
                />
            )}
            <CampusDataGrid
                repository={campaignsRepository}
                actionBarMeta={actionBarMeta}
                addSispMeta={addSispMeta}
                editSispMeta={{ sisp: CampaignsEditSisp }}
                dataGridMeta={dataGridMeta}
                deleteModalMeta={deleteModalMeta}
                promptMeta={businessDetailsCompleted ? promptMeta : returnCorrectBusinessDetailsNeededPrompt()}
            />
        </>
    );
};

export default CampaignsTable;
